/**
 * API Fetch wrapper with tenant header support
 * Automatically injects auth token and tenant ID
 */

import getApiUrl from './getApiUrl';

/**
 * Fetch wrapper that automatically adds auth and tenant headers
 * @param {string} url - API endpoint (relative to API_BASE)
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export async function apiFetch(url, options = {}) {
  const token = localStorage.getItem('auth_token');
  const tenantId = localStorage.getItem('active_tenant_id');

  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId;
  }

  const fullUrl = url.startsWith('http') ? url : `${getApiUrl()}${url}`;

  const response = await fetch(fullUrl, {
    ...options,
    credentials: 'include',
    headers,
  });

  return response;
}

/**
 * Fetch and parse JSON response
 * @param {string} url - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<object>}
 */
export async function apiFetchJson(url, options = {}) {
  const response = await apiFetch(url, options);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  return response.json();
}

/**
 * GET request helper
 */
export async function apiGet(url) {
  return apiFetchJson(url, { method: 'GET' });
}

/**
 * POST request helper
 */
export async function apiPost(url, data) {
  return apiFetchJson(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * PUT request helper
 */
export async function apiPut(url, data) {
  return apiFetchJson(url, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * DELETE request helper
 */
export async function apiDelete(url) {
  return apiFetchJson(url, { method: 'DELETE' });
}

/**
 * Get current tenant from localStorage
 */
export function getCurrentTenant() {
  const tenantId = localStorage.getItem('active_tenant_id');
  const tenantData = localStorage.getItem('active_tenant_data');
  
  if (tenantData) {
    try {
      return JSON.parse(tenantData);
    } catch (e) {
      return null;
    }
  }
  
  return tenantId ? { tenant_id: tenantId } : null;
}

/**
 * Set current tenant in localStorage
 */
export function setCurrentTenant(tenant) {
  if (tenant) {
    localStorage.setItem('active_tenant_id', tenant.tenant_id);
    localStorage.setItem('active_tenant_data', JSON.stringify(tenant));
  } else {
    localStorage.removeItem('active_tenant_id');
    localStorage.removeItem('active_tenant_data');
  }
}

/**
 * Initialize tenant on app load
 * Fetches user's tenants and sets default if needed
 */
export async function initializeTenant() {
  try {
    const response = await apiGet('/api/tenants/me');
    
    if (!response.success || !response.tenants || response.tenants.length === 0) {
      console.warn('No tenants available for user');
      return null;
    }
    
    const currentTenantId = localStorage.getItem('active_tenant_id');
    
    // Check if current tenant is still valid
    const validTenant = response.tenants.find(t => t.tenant_id === currentTenantId);
    
    if (validTenant) {
      setCurrentTenant(validTenant);
      return validTenant;
    }
    
    // Use default tenant or first available
    const defaultTenant = response.tenants.find(t => t.tenant_id === response.default_tenant_id)
      || response.tenants[0];
    
    setCurrentTenant(defaultTenant);
    return defaultTenant;
    
  } catch (error) {
    console.error('Failed to initialize tenant:', error);
    return null;
  }
}

export default apiFetch;
